export type DirectoryFilters = {
  focusId: string;
  productTypeId: string;
  /**
   * The cancer type to go back to when the treatment is cleared. The empty string means no
   * cancer type, and undefined means keep the one now showing, because the reader chose it.
   */
  restoreFocusId?: string;
};

/** The cancer type a treatment type belongs to, or undefined when it belongs to none. */
export type RequiredCancerFor = (productTypeId: string) => string | undefined;

/**
 * Builds the `RequiredCancerFor` for a loaded cancer type list. A treatment names its cancer
 * type by value, which is the same in every environment, and the id is read from the list, so
 * it is undefined until the list loads and when the list has no such type.
 */
export function requiredCancerFromLookup(
  lookup: ReadonlyMap<number, { value?: string | null }> | undefined,
  cancerValueFor: (productTypeId: string) => string | undefined
): RequiredCancerFor {
  const idByValue = new Map<string, string>();

  lookup?.forEach((cancerType, id) => {
    if (cancerType.value) {
      idByValue.set(cancerType.value, String(id));
    }
  });

  return (productTypeId) => {
    const value = cancerValueFor(productTypeId);

    return value ? idByValue.get(value) : undefined;
  };
}

/**
 * Whether a loaded cancer type list leaves the chosen treatment without a cancer type to search
 * within. False while the list is still loading, since that is not yet a failure.
 */
export function isTreatmentUnresolved(
  productTypeId: string,
  requiredCancerFor: RequiredCancerFor,
  listIsSettled: boolean
): boolean {
  return !!productTypeId && listIsSettled && !requiredCancerFor(productTypeId);
}

/**
 * The settled pair for a treatment read from the address before the cancer type list loaded, or
 * null while the list still cannot name that treatment's cancer type and nothing may change.
 */
export function settleAgainstCancerTypes(
  current: DirectoryFilters,
  requiredCancerFor: RequiredCancerFor
): DirectoryFilters | null {
  if (!current.productTypeId || !requiredCancerFor(current.productTypeId)) {
    return null;
  }

  return reconcileFilters(current, requiredCancerFor);
}

/**
 * How the directory's treatment and cancer type filters stay in step.
 *
 * A treatment type such as the bispecific antibodies one belongs to one cancer type, and the
 * search restricts results to it. Left implicit, the cancer dropdown read empty while the
 * results were limited to myeloma, and choosing another cancer type beside it produced a mix
 * the treatment's label did not describe. The rule is that a treatment always carries its
 * cancer type and the cancer dropdown shows it: choosing a treatment sets that cancer type,
 * and choosing a different cancer type, or clearing it, drops the treatment since it no longer
 * applies.
 *
 * The treatment is an override laid over the cancer type, so clearing it undoes the override.
 * Choosing the treatment remembers the cancer type it replaced, which may be none, and
 * clearing the treatment brings that back. A cancer type the reader chose themselves, including
 * the treatment's own, is never remembered for replacement and stays.
 */
export function chooseProductType(
  current: DirectoryFilters,
  productTypeId: string,
  requiredCancerFor: RequiredCancerFor
): DirectoryFilters {
  const required = requiredCancerFor(productTypeId);

  if (!productTypeId) {
    return {
      focusId: current.restoreFocusId ?? current.focusId,
      productTypeId: '',
      restoreFocusId: undefined,
    };
  }

  if (!required) {
    return { focusId: current.focusId, productTypeId, restoreFocusId: undefined };
  }

  if (current.productTypeId) {
    return { focusId: required, productTypeId, restoreFocusId: current.restoreFocusId };
  }

  return {
    focusId: required,
    productTypeId,
    restoreFocusId: current.focusId === required ? undefined : current.focusId,
  };
}

export function chooseCancerType(
  current: DirectoryFilters,
  focusId: string,
  requiredCancerFor: RequiredCancerFor
): DirectoryFilters {
  const required = requiredCancerFor(current.productTypeId);

  return {
    focusId,
    productTypeId: required && focusId !== required ? '' : current.productTypeId,
    restoreFocusId: undefined,
  };
}

/**
 * Settles a pair that arrived together, from a link or the address. A treatment alone gains
 * its cancer type, and clearing it clears that cancer type again. A treatment that conflicts
 * with the cancer type it came with is dropped, because the cancer type is what the link
 * named. A treatment that arrives with its own cancer type counts as the reader's choice.
 */
export function reconcileFilters(
  current: DirectoryFilters,
  requiredCancerFor: RequiredCancerFor
): DirectoryFilters {
  const required = requiredCancerFor(current.productTypeId);

  if (!required) {
    return current;
  }

  if (!current.focusId) {
    return { focusId: required, productTypeId: current.productTypeId, restoreFocusId: '' };
  }

  if (current.focusId !== required) {
    return { focusId: current.focusId, productTypeId: '', restoreFocusId: undefined };
  }

  return { ...current, restoreFocusId: undefined };
}
